import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { cn } from '../cn';

import { ChevronDownIcon } from './icons';
import { SidebarLink } from './SidebarLink';
import type { NavEntry } from './types';

interface Props {
  entry: NavEntry;
  visibleChildren: NavEntry[];
  collapsed: boolean;
}

export function SidebarGroup({
  entry,
  visibleChildren,
  collapsed,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const Icon = entry.icon;
  const label = t(entry.labelKey);
  const { pathname } = useLocation();

  const childActive = visibleChildren.some(
    (c) => c.to !== undefined && isPathActive(pathname, c.to, c.end),
  );
  const [open, setOpen] = useState(childActive);
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  if (collapsed) {
    return (
      <ul className="flex flex-col gap-1">
        {visibleChildren.map((c) => (
          <li key={c.id}>
            <SidebarLink entry={c} collapsed />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`nav-group-${entry.id}`}
        className={cn(
          'group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
          childActive
            ? 'font-medium text-foreground'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
      >
        <Icon size={18} className="shrink-0" />
        <span className="flex-1 truncate text-start">{label}</span>
        <ChevronDownIcon
          size={14}
          className={cn(
            'shrink-0 text-muted-foreground transition-transform',
            open ? '' : '-rotate-90',
          )}
        />
      </button>
      {open ? (
        <ul className="ms-4 mt-1 flex flex-col gap-1 border-s border-border ps-3">
          {visibleChildren.map((c) => (
            <li key={c.id}>
              <SubLink entry={c} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function SubLink({ entry }: { entry: NavEntry }): JSX.Element {
  const { t } = useTranslation();
  return (
    <NavLink
      to={entry.to ?? ''}
      end={entry.end ?? entry.to === ''}
      data-testid={`nav-${entry.id}`}
      className={({ isActive }) =>
        cn(
          'block rounded-md px-3 py-1.5 text-sm transition-colors',
          isActive
            ? 'bg-primary/10 font-medium text-foreground'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )
      }
    >
      {t(entry.labelKey)}
    </NavLink>
  );
}

function isPathActive(pathname: string, to: string, end?: boolean): boolean {
  if (end || to === '/' || to === '') return pathname === to;
  return pathname === to || pathname.startsWith(to + '/');
}
